import { useContext, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { ReactComponent as SecurityUploadIcon } from "../../../images/SecurityUploadIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import { ReactComponent as BusinessDocIcon } from "../../../images/BusinessDoc.svg";
import MessagePopup from "../../popup-components/MessagePopup";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import {
  comparisonRunSecurityMetrics,
  run2InsightsAndRecs,
  singleRunSASTInsightsAndRecs,
  singleRunSecurityMetrics,
} from "../../../services/webServices";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { useNavigate } from "react-router-dom";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import {
  SecurityFlowDataContext,
  SecurityLoaderStateContext,
} from "../../main-pages/SecurityTestingPage";

// PDF.js worker setup for client-side
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const toggleStyle = {
  flex: 1,
  "&.Mui-selected": {
    backgroundColor: "#e8f4fd",
    border: "2px solid #90caf9",
    "&:hover": { backgroundColor: "#e8f4fd" },
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#ffffff",
    border: "2px solid #e0e0e0",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
};
const toggleTitle = {
  fontSize: "18px",
  fontWeight: 600,
  textTransform: "none",
  color: "#000",
  mb: 1,
};
const toggleDesc = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  color: "#000",
  mb: 1,
};
const toggleHint = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  color: "#414141",
};

const SASTInsightsInputComponent = () => {
  const navigate = useNavigate();
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);

  const [run1File, setRun1File] = useState(null);
  const [run1Text, setRun1Text] = useState("");
  const [run1ParsedData, setRun1ParsedData] = useState(null);

  const [run2File, setRun2File] = useState(null);
  const [run2Text, setRun2Text] = useState("");
  const [run2ParsedData, setRun2ParsedData] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [scanner, setScanner] = useState("Single Run Analysis");
  const { setLoadingState } = useContext(LoadingModalContext);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { setInsightsPhase } = useContext(SecurityFlowDataContext);
  const { setSecurityLoaderState } = useContext(SecurityLoaderStateContext);

  // ----- HANDLERS FOR FILE UPLOAD/REMOVE -----
  const handleRun1Click = () =>
    fileInputRef1.current && fileInputRef1.current.click();
  const handleRun2Click = () =>
    fileInputRef2.current && fileInputRef2.current.click();

  const handleRemoveRun1File = () => {
    setRun1File(null);
    setRun1Text("");
    setRun1ParsedData(null);
    if (fileInputRef1.current) fileInputRef1.current.value = "";
    // For consistency: if Comparison Analysis and run2File gone, revert to Single Run
    if (!run2File) setScanner("Single Run Analysis");
  };
  const handleRemoveRun2File = () => {
    setRun2File(null);
    setRun2Text("");
    setRun2ParsedData(null);
    if (fileInputRef2.current) fileInputRef2.current.value = "";
    // If no Run1, revert to Single Run
    setScanner(run1File ? "Single Run Analysis" : "Single Run Analysis");
  };

  // const handleRun1FileChange = async (e) => {
  //   setOpenErrorPopup(false);
  //   setErrorMessage("");
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   try {
  //     let extractedText = "";
  //     let parsedData = null;

  //     if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
  //       // Use dedicated Run1 Excel parser
  //       parsedData = await readRun1ExcelFileAsJSON(file);
  //       extractedText = JSON.stringify(parsedData, null, 2);
  //     } else if (
  //       file.type === "application/pdf" ||
  //       file.name.endsWith(".pdf")
  //     ) {
  //       extractedText = await readPdfFileAsText(file);
  //       parsedData = extractedText;
  //     } else if (
  //       file.type ===
  //         "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  //       file.name.endsWith(".docx")
  //     ) {
  //       extractedText = await readDocxFileAsText(file);
  //       parsedData = extractedText;
  //     } else {
  //       extractedText = await readPlainTextFile(file);
  //       parsedData = extractedText;
  //     }

  //     if (extractedText) {
  //       setRun1File(file);
  //       setRun1Text(extractedText);
  //       setRun1ParsedData(parsedData || {});
  //       if (run2File) setScanner("Comparison Analysis");
  //     }
  //   } catch (err) {
  //     setOpenErrorPopup(true);
  //     setErrorMessage("Error processing Run-1 file: " + err.message);
  //   }
  // };

  // const handleRun2FileChange = async (e) => {
  //   setOpenErrorPopup(false);
  //   setErrorMessage("");
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   try {
  //     let extractedText = "";
  //     let parsedData = null;

  //     if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
  //       // Use dedicated Run2 Excel parser
  //       parsedData = await readRun2ExcelFileAsJSON(file);
  //       extractedText = JSON.stringify(parsedData, null, 2);
  //     } else if (
  //       file.type === "application/pdf" ||
  //       file.name.endsWith(".pdf")
  //     ) {
  //       extractedText = await readPdfFileAsText(file);
  //       parsedData = extractedText;
  //     } else if (
  //       file.type ===
  //         "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  //       file.name.endsWith(".docx")
  //     ) {
  //       extractedText = await readDocxFileAsText(file);
  //       parsedData = extractedText;
  //     } else {
  //       extractedText = await readPlainTextFile(file);
  //       parsedData = extractedText;
  //     }

  //     if (extractedText) {
  //       setRun2File(file);
  //       setRun2Text(extractedText);
  //       setRun2ParsedData(parsedData || {});
  //       if (run1File) setScanner("Comparison Analysis");
  //     }
  //   } catch (err) {
  //     setOpenErrorPopup(true);
  //     setErrorMessage("Error processing Run-2 file: " + err.message);
  //   }
  // };

  // const readRun1ExcelFileAsJSON = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       const data = new Uint8Array(e.target.result);
  //       const workbook = XLSX.read(data, { type: "array" });
  //       const sheet = workbook.Sheets[workbook.SheetNames[0]];
  //       const rawData = XLSX.utils.sheet_to_json(sheet, {
  //         header: 1,
  //         defval: "",
  //         blankrows: false,
  //       });

  //       const headers = rawData[0];
  //       const dataRows = rawData.slice(1);

  //       const results = [];
  //       let currentVuln = null;

  //       function parseDate(value) {
  //         if (!value) return null;
  //         if (typeof value === "number") {
  //           const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  //           const date = new Date(excelEpoch.getTime() + value * 86400 * 1000);
  //           return date.toISOString().split("T")[0];
  //         }
  //         if (typeof value === "string") {
  //           const parsed = new Date(value);
  //           if (!isNaN(parsed.getTime()))
  //             return parsed.toISOString().split("T")[0];
  //           return value;
  //         }
  //         return null;
  //       }

  //       for (const row of dataRows) {
  //         const vulnerabilityName =
  //           row[headers.indexOf("Vulnerability Name")] || "";
  //         const description = row[headers.indexOf("Description")] || "";
  //         const filePath = row[headers.indexOf("Filepath")] || "";
  //         const lineNumber = row[headers.indexOf("Line Number")] || "";
  //         const severity = row[headers.indexOf("Severity")] || "";
  //         const recommendations = row[headers.indexOf("Recomendations")] || "";
  //         const identifiedDateRaw =
  //           row[headers.indexOf("Vulnerability Identified Date")] || "";
  //         const instanceStatus =
  //           row[headers.indexOf("Vulnerability Status")] || ""; // Expected "TP", "FP"
  //         const totalLinesOfCode =
  //           row[headers.indexOf("Total Lines of Code (LoC)")] || "";

  //         if (vulnerabilityName && description) {
  //           currentVuln = {
  //             vulnerabilityName,
  //             description,
  //             severity,
  //             recommendations,
  //             vulnerabilityIdentifiedDate: parseDate(identifiedDateRaw),
  //             totalLinesOfCode,
  //             instances: [],
  //           };
  //           results.push(currentVuln);
  //         }

  //         if (currentVuln && filePath) {
  //           currentVuln.instances.push({
  //             filePath,
  //             lineNumber,
  //             vulnerabilityStatus: instanceStatus,
  //           });
  //         }
  //       }

  //       resolve(results);
  //     };

  //     reader.onerror = (err) => reject(err);

  //     reader.readAsArrayBuffer(file);
  //   });

  // const readRun2ExcelFileAsJSON = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       const data = new Uint8Array(e.target.result);
  //       const workbook = XLSX.read(data, { type: "array" });
  //       const sheet = workbook.Sheets[workbook.SheetNames[0]];
  //       const rawData = XLSX.utils.sheet_to_json(sheet, {
  //         header: 1,
  //         defval: "",
  //         blankrows: false,
  //       });

  //       const headers = rawData[0];
  //       const dataRows = rawData.slice(1);

  //       const results = [];
  //       let currentVuln = null;

  //       function parseDate(value) {
  //         if (!value) return null;

  //         // Handle Excel numeric dates (days since 1899-12-30)
  //         if (typeof value === "number") {
  //           const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Excel date epoch
  //           const date = new Date(excelEpoch.getTime() + value * 86400 * 1000);
  //           return date.toISOString().split("T")[0]; // Return YYYY-MM-DD
  //         }

  //         // Handle string dates:
  //         if (typeof value === "string") {
  //           // Try parsing "DD-MM-YYYY" or "YYYY-MM-DD"
  //           // Detect if string is in DD-MM-YYYY format (common with dashes and day > 12)
  //           const dashParts = value.split("-");
  //           if (dashParts.length === 3) {
  //             const [part1, part2, part3] = dashParts;

  //             // Simple heuristic: if first part length is 4 -> assume YYYY-MM-DD
  //             // else assume DD-MM-YYYY and convert
  //             if (part1.length === 4) {
  //               // Format likely YYYY-MM-DD, try parsing directly
  //               const parsed = new Date(value);
  //               if (!isNaN(parsed.getTime()))
  //                 return parsed.toISOString().split("T")[0];
  //             } else {
  //               // Assume DD-MM-YYYY -> rearrange to YYYY-MM-DD for parsing
  //               const isoFormatted = `${part3}-${part2}-${part1}`;
  //               const parsed = new Date(isoFormatted);
  //               if (!isNaN(parsed.getTime()))
  //                 return parsed.toISOString().split("T")[0];
  //             }
  //           }

  //           // Fallback: try native Date parse for other formats
  //           const fallbackParsed = new Date(value);
  //           if (!isNaN(fallbackParsed.getTime())) {
  //             return fallbackParsed.toISOString().split("T")[0];
  //           }

  //           // If cannot parse, return as-is (or null)
  //           return null;
  //         }

  //         // Unknown format, return null
  //         return null;
  //       }

  //       for (const row of dataRows) {
  //         const vulnerabilityName =
  //           row[headers.indexOf("Vulnerability Name")] || "";
  //         const description = row[headers.indexOf("Description")] || "";
  //         const filePath = row[headers.indexOf("Filepath")] || "";
  //         const lineNumber = row[headers.indexOf("Line Number")] || "";
  //         const severity = row[headers.indexOf("Severity")] || "";
  //         const recommendation = row[headers.indexOf("Recommendation")] || "";
  //         const identifiedDateRaw =
  //           row[headers.indexOf("Vulnerability Identified date")] || "";
  //         const instanceStatus =
  //           row[headers.indexOf("Vulnerability Status")] || ""; // "TP"/"FP"
  //         const remediationStatus =
  //           row[headers.indexOf("Vulnerability Remediation Status")] || "";
  //         const remediationDateRaw =
  //           row[headers.indexOf("Vulnerability Remediation date")] || "";
  //         const totalLinesOfCode =
  //           row[headers.indexOf("Total Lines of Code (LoC)")] || "";

  //         if (vulnerabilityName && description) {
  //           currentVuln = {
  //             vulnerabilityName,
  //             description,
  //             severity,
  //             recommendation,
  //             vulnerabilityIdentifiedDate: parseDate(identifiedDateRaw),
  //             totalLinesOfCode,
  //             instances: [],
  //             remediationStatus,
  //             remediationDate: parseDate(remediationDateRaw),
  //           };
  //           results.push(currentVuln);
  //         }

  //         if (currentVuln && filePath) {
  //           currentVuln.instances.push({
  //             filePath,
  //             lineNumber,
  //             vulnerabilityStatus: instanceStatus,
  //           });
  //         }
  //       }

  //       resolve(results);
  //     };

  //     reader.onerror = (err) => reject(err);

  //     reader.readAsArrayBuffer(file);
  //   });

  // ---- SCAN LOGIC ----
  // const handleInitiateScan = async () => {
  //   if (scanner === "Single Run Analysis" && !run1File) {
  //     setOpenErrorPopup(true);
  //     setErrorMessage("Please upload a SAST report for Single Run Analysis.");
  //     return;
  //   }
  //   if (scanner === "Comparison Analysis" && (!run1File || !run2File)) {
  //     setOpenErrorPopup(true);
  //     setErrorMessage(
  //       "Please upload both SAST reports for Comparison Analysis."
  //     );
  //     return;
  //   }

  //   let fake;
  //   const startProgress = (setProgress) => {
  //     let progress = 0;
  //     let stop = false;
  //     let finalMessageShown = false;
  //     const interval = setInterval(() => {
  //       if (stop) return;
  //       progress += 0.5;
  //       if (progress >= 97 && !finalMessageShown) {
  //         finalMessageShown = true;
  //         setLoadingState((prev) => ({
  //           ...prev,
  //           message: "Almost done! Fetching your output",
  //         }));
  //       }
  //       if (progress >= 97) progress = 97;
  //       setProgress((prev) => ({
  //         ...prev,
  //         progress: Math.max(prev.progress, Math.floor(progress)),
  //       }));
  //     }, 200);
  //     return {
  //       stop: () => {
  //         stop = true;
  //         clearInterval(interval);
  //       },
  //       completeTo100: async () => {
  //         stop = true;
  //         clearInterval(interval);
  //         setProgress((prev) => ({
  //           ...prev,
  //           progress: 100,
  //         }));
  //         await new Promise((res) => setTimeout(res, 500));
  //       },
  //     };
  //   };

  //   setLoadingState({
  //     open: true,
  //     message: "Generating SAST Insights",
  //     progress: 0,
  //   });
  //   fake = startProgress(setLoadingState);

  //   try {
  //     if (scanner === "Single Run Analysis") {
  //       // console.log("run1ParsedData", run1ParsedData);
  //       const securityMetricsResponse = await singleRunSecurityMetrics({
  //         run: run1ParsedData,
  //       });
  //       const run1InsightsAndRecs = await singleRunSASTInsightsAndRecs({
  //         run1Metrics: securityMetricsResponse.data?.run1Metrics,
  //       });
  //       navigate("/dashboard/security-testing/sast-insights-dashboard", {
  //         state: {
  //           runAnalysis: "Single Run Analysis",
  //           securityMetrics: securityMetricsResponse.data?.run1Metrics,
  //           insightsAndRecs: run1InsightsAndRecs.data?.insightsAndRecs,
  //         },
  //       });
  //     } else if (scanner === "Comparison Analysis") {
  //       // console.log("run1ParsedData", run1ParsedData);
  //       // console.log("run2ParsedData", run2ParsedData);
  //       const securityMetricsResponse = await singleRunSecurityMetrics({
  //         run: run1ParsedData,
  //       });
  //       // console.log("run1Metrics", securityMetricsResponse.data?.run1Metrics);
  //       const comparisonMetricsResponse = await comparisonRunSecurityMetrics({
  //         run1: run1ParsedData,
  //         run2: run2ParsedData,
  //       });
  //       // console.log("run2Metrics", comparisonMetricsResponse.data?.run2Metrics);
  //       const insightsAndRecs = await run2InsightsAndRecs({
  //         run1Metrics: securityMetricsResponse.data?.run1Metrics,
  //         run2Metrics: comparisonMetricsResponse.data?.run2Metrics,
  //       });
  //       // console.log("run2Isnights", insightsAndRecs);
  //       navigate("/dashboard/security-testing/sast-insights-dashboard", {
  //         state: {
  //           runAnalysis: "Comparison Analysis",
  //           securityMetrics: {
  //             run1Metrics: securityMetricsResponse.data?.run1Metrics,
  //             run2Metrics: comparisonMetricsResponse.data?.run2Metrics,
  //           },
  //           insightsAndRecs: insightsAndRecs.data?.combinedInsights,
  //         },
  //       });
  //     }
  //     fake?.stop();
  //     setLoadingState({ open: false, message: "", progress: null });
  //   } catch (error) {
  //     fake?.stop();
  //     if (error.response?.status === 401) {
  //       setSessionExpired(true);
  //     } else {
  //       setErrorMessage(
  //         error.response?.data?.message ||
  //           "An error occurred while starting the scan."
  //       );
  //     }
  //     setLoadingState({ open: false, message: "", progress: null });
  //     setOpenErrorPopup(true);
  //   }
  // };

  // const readPdfFileAsText = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = async (e) => {
  //       const typedArray = new Uint8Array(e.target.result);
  //       const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
  //       let textOutput = "";
  //       for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
  //         const page = await pdf.getPage(pageNum);
  //         const content = await page.getTextContent();
  //         const strings = content.items.map((item) => item.str);
  //         textOutput += strings.join(" ") + "\n";
  //       }
  //       resolve(textOutput);
  //     };
  //     reader.onerror = (err) => reject(err);
  //     reader.readAsArrayBuffer(file);
  //   });

  // const readDocxFileAsText = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = async (e) => {
  //       const arrayBuffer = e.target.result;
  //       const result = await mammoth.extractRawText({ arrayBuffer });
  //       resolve(result.value);
  //     };
  //     reader.onerror = (err) => reject(err);
  //     reader.readAsArrayBuffer(file);
  //   });

  // const readPlainTextFile = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = (err) => reject(err);
  //     reader.readAsText(file);
  //   });

  // ---- RENDER ----
  // Validation utility function (restrict non-xlsx files)

  const validateFileType = (file, runType = "Run-1") => {
    if (!file) {
      return { isValid: false, error: "No file selected" };
    }

    if (file.size === 0) {
      return { isValid: false, error: "File appears to be empty or corrupted" };
    }

    // Check if it's .xlsx - if yes, accept without validation
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith(".xlsx")) {
      return { isValid: true }; // Accept .xlsx files without further validation
    }

    // Restrict all other file types
    const rejectedExtensions = [
      ".xls",
      ".pdf",
      ".docx",
      ".doc",
      ".txt",
      ".csv",
      ".json",
      ".xml",
    ];
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));

    if (rejectedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File type ${fileExtension} is not supported for ${runType}. Only .xlsx files are allowed.`,
      };
    }

    // For any other unknown file types
    return {
      isValid: false,
      error: `Unsupported file type for ${runType}. Only .xlsx files are supported. Selected file: ${file.name}`,
    };
  };

  // Enhanced Run1 file handler (accept .xlsx, restrict others)
  const handleRun1FileChange = async (e) => {
    setOpenErrorPopup(false);
    setErrorMessage("");

    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (restrict non-.xlsx files)
    const validation = validateFileType(file, "Run-1");
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      setOpenErrorPopup(true);
      e.target.value = ""; // Reset file input
      return;
    }

    try {
      // Process .xlsx files directly without additional validation
      const parsedData = await readRun1ExcelFileAsJSON(file);

      // Basic data validation
      if (!parsedData || parsedData.length === 0) {
        throw new Error("No valid vulnerability data found in the Excel file");
      }

      const extractedText = JSON.stringify(parsedData, null, 2);

      // Success - update state
      setRun1File(file);
      setRun1Text(extractedText);
      setRun1ParsedData(parsedData);

      if (run2File) setScanner("Comparison Analysis");

      console.log(
        `Run-1 file processed successfully: ${parsedData.length} vulnerabilities found`
      );
    } catch (err) {
      console.error("Run-1 file processing error:", err);

      let errorMsg = "Error processing Run-1 file: ";
      errorMsg +=
        err.message || "Unknown error occurred during file processing";

      setErrorMessage(errorMsg);
      setOpenErrorPopup(true);
      e.target.value = ""; // Reset file input
    }
  };

  // Enhanced Run2 file handler (accept .xlsx, restrict others)
  const handleRun2FileChange = async (e) => {
    setOpenErrorPopup(false);
    setErrorMessage("");

    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (restrict non-.xlsx files)
    const validation = validateFileType(file, "Run-2");
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      setOpenErrorPopup(true);
      e.target.value = ""; // Reset file input
      return;
    }

    try {
      // Process .xlsx files directly without additional validation
      const parsedData = await readRun2ExcelFileAsJSON(file);

      // Basic data validation
      if (!parsedData || parsedData.length === 0) {
        throw new Error("No valid vulnerability data found in the Excel file");
      }

      const extractedText = JSON.stringify(parsedData, null, 2);

      // Success - update state
      setRun2File(file);
      setRun2Text(extractedText);
      setRun2ParsedData(parsedData);

      if (run1File) setScanner("Comparison Analysis");

      console.log(
        `Run-2 file processed successfully: ${parsedData.length} vulnerabilities found`
      );
    } catch (err) {
      console.error("Run-2 file processing error:", err);

      let errorMsg = "Error processing Run-2 file: ";
      errorMsg +=
        err.message || "Unknown error occurred during file processing";

      setErrorMessage(errorMsg);
      setOpenErrorPopup(true);
      e.target.value = ""; // Reset file input
    }
  };

  // Simplified Excel readers (removed extensive validation for .xlsx files)
  const readRun1ExcelFileAsJSON = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rawData = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            blankrows: false,
          });

          const headers = rawData[0];
          const dataRows = rawData.slice(1);

          const results = [];
          let currentVuln = null;

          function parseDate(value) {
            if (!value) return null;
            if (typeof value === "number") {
              const excelEpoch = new Date(Date.UTC(1899, 11, 30));
              const date = new Date(
                excelEpoch.getTime() + value * 86400 * 1000
              );
              return date.toISOString().split("T")[0];
            }
            if (typeof value === "string") {
              const parsed = new Date(value);
              if (!isNaN(parsed.getTime()))
                return parsed.toISOString().split("T")[0];
              return value;
            }
            return null;
          }

          for (const row of dataRows) {
            const vulnerabilityName =
              row[headers.indexOf("Vulnerability Name")] || "";
            const description = row[headers.indexOf("Description")] || "";
            const filePath = row[headers.indexOf("Filepath")] || "";
            const lineNumber = row[headers.indexOf("Line Number")] || "";
            const severity = row[headers.indexOf("Severity")] || "";
            const recommendations =
              row[headers.indexOf("Recomendations")] || "";
            const identifiedDateRaw =
              row[headers.indexOf("Vulnerability Identified Date")] || "";
            const instanceStatus =
              row[headers.indexOf("Vulnerability Status")] || "";
            const totalLinesOfCode =
              row[headers.indexOf("Total Lines of Code (LoC)")] || "";

            if (vulnerabilityName && description) {
              currentVuln = {
                vulnerabilityName,
                description,
                severity,
                recommendations,
                vulnerabilityIdentifiedDate: parseDate(identifiedDateRaw),
                totalLinesOfCode,
                instances: [],
              };
              results.push(currentVuln);
            }

            if (currentVuln && filePath) {
              currentVuln.instances.push({
                filePath,
                lineNumber,
                vulnerabilityStatus: instanceStatus,
              });
            }
          }

          resolve(results);
        } catch (error) {
          reject(new Error(`Excel parsing failed: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file. The file may be corrupted."));
      };

      reader.readAsArrayBuffer(file);
    });

  const readRun2ExcelFileAsJSON = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rawData = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            blankrows: false,
          });

          const headers = rawData[0];
          const dataRows = rawData.slice(1);

          const results = [];
          let currentVuln = null;

          function parseDate(value) {
            if (!value) return null;

            if (typeof value === "number") {
              const excelEpoch = new Date(Date.UTC(1899, 11, 30));
              const date = new Date(
                excelEpoch.getTime() + value * 86400 * 1000
              );
              return date.toISOString().split("T")[0];
            }

            if (typeof value === "string") {
              const dashParts = value.split("-");
              if (dashParts.length === 3) {
                const [part1, part2, part3] = dashParts;

                if (part1.length === 4) {
                  const parsed = new Date(value);
                  if (!isNaN(parsed.getTime()))
                    return parsed.toISOString().split("T")[0];
                } else {
                  const isoFormatted = `${part3}-${part2}-${part1}`;
                  const parsed = new Date(isoFormatted);
                  if (!isNaN(parsed.getTime()))
                    return parsed.toISOString().split("T")[0];
                }
              }

              const fallbackParsed = new Date(value);
              if (!isNaN(fallbackParsed.getTime())) {
                return fallbackParsed.toISOString().split("T")[0];
              }

              return null;
            }

            return null;
          }

          for (const row of dataRows) {
            const vulnerabilityName =
              row[headers.indexOf("Vulnerability Name")] || "";
            const description = row[headers.indexOf("Description")] || "";
            const filePath = row[headers.indexOf("Filepath")] || "";
            const lineNumber = row[headers.indexOf("Line Number")] || "";
            const severity = row[headers.indexOf("Severity")] || "";
            const recommendation = row[headers.indexOf("Recommendation")] || "";
            const identifiedDateRaw =
              row[headers.indexOf("Vulnerability Identified date")] || "";
            const instanceStatus =
              row[headers.indexOf("Vulnerability Status")] || "";
            const remediationStatus =
              row[headers.indexOf("Vulnerability Remediation Status")] || "";
            const remediationDateRaw =
              row[headers.indexOf("Vulnerability Remediation date")] || "";
            const totalLinesOfCode =
              row[headers.indexOf("Total Lines of Code (LoC)")] || "";

            if (vulnerabilityName && description) {
              currentVuln = {
                vulnerabilityName,
                description,
                severity,
                recommendation,
                vulnerabilityIdentifiedDate: parseDate(identifiedDateRaw),
                totalLinesOfCode,
                instances: [],
                remediationStatus,
                remediationDate: parseDate(remediationDateRaw),
              };
              results.push(currentVuln);
            }

            if (currentVuln && filePath) {
              currentVuln.instances.push({
                filePath,
                lineNumber,
                vulnerabilityStatus: instanceStatus,
              });
            }
          }

          resolve(results);
        } catch (error) {
          reject(new Error(`Excel parsing failed: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file. The file may be corrupted."));
      };

      reader.readAsArrayBuffer(file);
    });

  const handleInitiateScan = async () => {
    // Initial validation
    if (scanner === "Single Run Analysis" && !run1File) {
      setOpenErrorPopup(true);
      setErrorMessage(
        "Please upload a SAST report (.xlsx file) for Single Run Analysis."
      );
      return;
    }

    if (scanner === "Comparison Analysis" && (!run1File || !run2File)) {
      setOpenErrorPopup(true);
      setErrorMessage(
        "Please upload both SAST reports (.xlsx files) for Comparison Analysis."
      );
      return;
    }

    // Validate parsed data exists
    if (
      scanner === "Single Run Analysis" &&
      (!run1ParsedData || run1ParsedData.length === 0)
    ) {
      setOpenErrorPopup(true);
      setErrorMessage(
        "Run-1 file appears to be empty or invalid. Please upload a valid SAST report."
      );
      return;
    }

    if (scanner === "Comparison Analysis") {
      if (!run1ParsedData || run1ParsedData.length === 0) {
        setOpenErrorPopup(true);
        setErrorMessage(
          "Run-1 file appears to be empty or invalid. Please upload a valid SAST report."
        );
        return;
      }
      if (!run2ParsedData || run2ParsedData.length === 0) {
        setOpenErrorPopup(true);
        setErrorMessage(
          "Run-2 file appears to be empty or invalid. Please upload a valid SAST report."
        );
        return;
      }
    }

    
    setSecurityLoaderState("Insights Scan");


    let progressController;
    let currentStep = 0;

    const scanSteps = {
      single: [
        "Initializing security analysis...",
        "Parsing vulnerability data...",
        "Calculating security metrics...",
        "Generating insights and recommendations...",
        "Preparing dashboard...",
      ],
      comparison: [
        "Initializing comparison analysis...",
        "Processing Run-1 data...",
        "Processing Run-2 data...",
        "Calculating security metrics...",
        "Performing comparison analysis...",
        "Generating insights and recommendations...",
        "Preparing dashboard...",
      ],
    };

    const steps =
      scanner === "Single Run Analysis"
        ? scanSteps.single
        : scanSteps.comparison;

    // Enhanced progress controller
    const createProgressController = () => {
      let progress = 0;
      let stop = false;
      let stepInterval;

      const updateStep = () => {
        if (stop || currentStep >= steps.length - 1) return;

        currentStep++;
        setLoadingState((prev) => ({
          ...prev,
          message: steps[currentStep],
        }));
      };

      const interval = setInterval(() => {
        if (stop) return;

        const stepProgress = 95 / steps.length;
        const targetProgress = Math.min((currentStep + 1) * stepProgress, 95);

        if (progress < targetProgress) {
          progress += 0.8;
          setLoadingState((prev) => ({
            ...prev,
            progress: Math.min(Math.floor(progress), 95),
          }));
        }
      }, 150);

      // Step progression
      stepInterval = setInterval(updateStep, 2000);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
          clearInterval(stepInterval);
        },
        nextStep: (message) => {
          if (stop) return;
          currentStep = Math.min(currentStep + 1, steps.length - 1);
          setLoadingState((prev) => ({
            ...prev,
            message: message || steps[currentStep],
            progress: Math.min(
              Math.floor(((currentStep + 1) / steps.length) * 90),
              95
            ),
          }));
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          clearInterval(stepInterval);

          setLoadingState((prev) => ({
            ...prev,
            message: "Complete! Redirecting to dashboard...",
            progress: 100,
          }));

          await new Promise((resolve) => setTimeout(resolve, 800));
        },
      };
    };

    // Initialize loading state
    setLoadingState({
      open: true,
      message: steps[0],
      progress: 0,
    });

    progressController = createProgressController();

    try {
      if (scanner === "Single Run Analysis") {

        // Step 1: Calculate security metrics
        progressController.nextStep("Calculating security metrics...");
        const securityMetricsResponse = await singleRunSecurityMetrics({
          run: run1ParsedData,
        });

        if (!securityMetricsResponse?.data?.run1Metrics) {
          throw new Error(
            "Failed to calculate security metrics. Invalid response from server."
          );
        }

        // Step 2: Generate insights
        progressController.nextStep(
          "Generating insights and recommendations..."
        );

        const run1InsightsAndRecs = await singleRunSASTInsightsAndRecs({
          run1Metrics: securityMetricsResponse.data.run1Metrics,
        });

        if (!run1InsightsAndRecs?.data?.insightsAndRecs) {
          throw new Error("Failed to generate insights and recommendations.");
        }

        // Step 3: Complete and navigate
        await progressController.completeTo100();

        setInsightsPhase({
          securityMetrics: securityMetricsResponse.data.run1Metrics,
          insightsAndRecs: run1InsightsAndRecs.data.insightsAndRecs,
        });



        navigate("/dashboard/security-testing/sast-insights-dashboard", {
          state: {
            runAnalysis: "Single Run Analysis",
            securityMetrics: securityMetricsResponse.data.run1Metrics,
            insightsAndRecs: run1InsightsAndRecs.data.insightsAndRecs,
            uploadedFiles: {
              run1: {
                name: run1File.name,
                size: run1File.size,
                uploadTime: new Date().toISOString(),
              },
            },
          },
        });
      } else if (scanner === "Comparison Analysis") {


        // Step 1: Process Run-1
        progressController.nextStep("Processing Run-1 data...");
        const securityMetricsResponse = await singleRunSecurityMetrics({
          run: run1ParsedData,
        });

        if (!securityMetricsResponse?.data?.run1Metrics) {
          throw new Error(
            "Failed to process Run-1 data. Invalid response from server."
          );
        }

        // Step 2: Process Run-2 and comparison
        progressController.nextStep("Processing Run-2 data and comparison...");
        const comparisonMetricsResponse = await comparisonRunSecurityMetrics({
          run1: run1ParsedData,
          run2: run2ParsedData,
        });

        if (!comparisonMetricsResponse?.data?.run2Metrics) {
          throw new Error(
            "Failed to process Run-2 data and comparison. Invalid response from server."
          );
        }

        // Step 3: Generate insights
        progressController.nextStep("Generating comprehensive insights...");
        const insightsAndRecs = await run2InsightsAndRecs({
          run1Metrics: securityMetricsResponse.data.run1Metrics,
          run2Metrics: comparisonMetricsResponse.data.run2Metrics,
        });

        if (!insightsAndRecs?.data?.combinedInsights) {
          throw new Error(
            "Failed to generate comparison insights and recommendations."
          );
        }

        // Step 4: Complete and navigate
        await progressController.completeTo100();


        // console.log("Comparison Analysis completed successfully");
        setInsightsPhase({
          securityMetrics: securityMetricsResponse.data.run1Metrics,
          insightsAndRecs: insightsAndRecs.data.combinedInsights,
        });


        navigate("/dashboard/security-testing/sast-insights-dashboard", {
          state: {
            runAnalysis: "Comparison Analysis",
            securityMetrics: {
              run1Metrics: securityMetricsResponse.data.run1Metrics,
              run2Metrics: comparisonMetricsResponse.data.run2Metrics,
            },
            insightsAndRecs: insightsAndRecs.data.combinedInsights,
            uploadedFiles: {
              run1: {
                name: run1File.name,
                size: run1File.size,
                uploadTime: new Date().toISOString(),
              },
              run2: {
                name: run2File.name,
                size: run2File.size,
                uploadTime: new Date().toISOString(),
              },
            },
          },
        });
      }

      // Reset loading state
      setLoadingState({ open: false, message: "", progress: null });
    } catch (error) {
      // Stop progress controller
      progressController?.stop();

      console.error(`${scanner} error:`, error);

      // Handle different error types
      let errorMessage =
        "An unexpected error occurred while processing the scan.";
      let shouldShowSessionExpired = false;

      if (error.response) {
        // Server response errors
        const status = error.response.status;
        const serverMessage = error.response.data?.message;

        switch (status) {
          case 401:
            shouldShowSessionExpired = true;
            break;
          case 400:
            errorMessage =
              serverMessage ||
              "Invalid request. Please check your uploaded files and try again.";
            break;
          case 422:
            errorMessage =
              serverMessage ||
              "The uploaded file format is not supported or contains invalid data.";
            break;
          case 500:
            errorMessage =
              "Server error occurred. Please try again later or contact support.";
            break;
          case 503:
            errorMessage =
              "Service temporarily unavailable. Please try again in a few minutes.";
            break;
          default:
            errorMessage =
              serverMessage || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network errors
        errorMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (error.message) {
        // Custom error messages
        errorMessage = error.message;
      }

      // Handle session expiration
      if (shouldShowSessionExpired) {
        setSessionExpired(true);
      } else {
        setErrorMessage(errorMessage);
        setOpenErrorPopup(true);
      }

      // Reset loading state
      setLoadingState({ open: false, message: "", progress: null });

      // Log detailed error for debugging
      console.error("Scan Error Details:", {
        scanner,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        run1DataLength: run1ParsedData?.length,
        run2DataLength: run2ParsedData?.length,
        timestamp: new Date().toISOString(),
      });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          height: { xs: "auto", md: "15vh" },
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          padding: 2,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            width: "100%",
            gap: 2,
            p: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "27px",
                fontWeight: 700,
                color: "#000000",
              }}
            >
              SAST Metrics Analyzer
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#000000",
                fontWeight: 400,
                mt: 2,
              }}
            >
              Scan Analysis & Intelligence Platform
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: 2,
          backgroundColor: "#FFFFFF",
          minHeight: "85vh",
          m: 4,
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "38px",
              color: "#000000",
              fontWeight: 600,
            }}
          >
            Analyze Your SAST Security Reports
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              color: "#000000",
              fontWeight: 500,
              mt: 2,
            }}
          >
            Upload your Static Application Security Testing (SAST) reports to
            get comprehensive security metrics and insights, and actionable
            recommendations for your codebase.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          {/* Toggle Tabs */}
          <Box sx={{ display: "flex", gap: 2, mt: 4, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <ToggleButtonGroup
                exclusive
                value={scanner}
                onChange={(e, newScanner) => {
                  if (newScanner) {
                    setScanner(newScanner);
                    // Reset files on toggle change!
                    setRun1File(null);
                    setRun1Text("");
                    setRun1ParsedData(null);
                    setRun2File(null);
                    setRun2Text("");
                    setRun2ParsedData(null);
                    if (fileInputRef1.current) fileInputRef1.current.value = "";
                    if (fileInputRef2.current) fileInputRef2.current.value = "";
                  }
                }}
                fullWidth
                sx={{ height: "183px" }}
              >
                <ToggleButton value="Single Run Analysis" sx={toggleStyle}>
                  <Box>
                    <Typography sx={toggleTitle}>
                      Single Run Analysis
                    </Typography>
                    <Typography sx={toggleDesc}>
                      Analyze a single SAST scan run for comprehensive security
                      metrics
                    </Typography>
                    <Typography sx={toggleHint}>
                      Requires a single SAST report
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ flex: 1 }}>
              <ToggleButtonGroup
                exclusive
                value={scanner}
                onChange={(e, newScanner) => {
                  if (newScanner) {
                    setScanner(newScanner);
                    // Reset files on toggle change!
                    setRun1File(null);
                    setRun1Text("");
                    setRun1ParsedData(null);
                    setRun2File(null);
                    setRun2Text("");
                    setRun2ParsedData(null);
                    if (fileInputRef1.current) fileInputRef1.current.value = "";
                    if (fileInputRef2.current) fileInputRef2.current.value = "";
                  }
                }}
                fullWidth
                sx={{ height: "183px" }}
              >
                <ToggleButton value="Comparison Analysis" sx={toggleStyle}>
                  <Box>
                    <Typography sx={toggleTitle}>
                      Comparison Analysis
                    </Typography>
                    <Typography sx={toggleDesc}>
                      Compare two SAST scan runs to analyze security
                      improvements and trends
                    </Typography>
                    <Typography sx={toggleHint}>
                      Requires 2 SAST reports
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Run1 Upload Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
            }}
          >
            <Typography
              sx={{
                fontSize: "23px",
                color: "#0094FB",
                fontWeight: 600,
              }}
            >
              Upload Run-1 SAST Report
            </Typography>
            {!run1File ? (
              <Paper
                variant="outlined"
                sx={{
                  pt: 2,
                  pb: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed #90caf9",
                  cursor: "pointer",
                  mb: 4,
                  mt: 2,
                  backgroundColor: "#EAF4FF",
                  width: "100%",
                  height: "119px",
                  position: "relative",
                  gap: 2,
                }}
                onClick={handleRun1Click}
              >
                <SecurityUploadIcon width={24} height={24} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      color: "black",
                      ":hover": { textDecoration: "underline" },
                    }}
                  >
                    Drop your Run-1 SAST report here or click to browse
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supports XLSX format. Upload a file
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef1}
                  type="file"
                  accept=".xlsx,.xls,"
                  hidden
                  onChange={handleRun1FileChange}
                />
              </Paper>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "2px solid #90caf9",
                    mb: 2,
                    backgroundColor: "#EAF4FF",
                    width: "100%",
                    position: "relative",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <BusinessDocIcon width={24} height={24} />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "black",
                        }}
                      >
                        {run1File.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#00000080" }}>
                        {(run1File.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    aria-label="delete"
                    onClick={handleRemoveRun1File}
                  >
                    <DeleteIcon sx={{ color: "#f44336" }} />
                  </IconButton>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Run2 Upload Section - Only show if Comparison Analysis is selected */}
          {scanner === "Comparison Analysis" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 4,
              }}
            >
              <Typography
                sx={{
                  fontSize: "23px",
                  color: "#0094FB",
                  fontWeight: 600,
                }}
              >
                Upload Run-2 SAST Report
              </Typography>
              {!run2File ? (
                <Paper
                  variant="outlined"
                  sx={{
                    pt: 2,
                    pb: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #90caf9",
                    cursor: "pointer",
                    mb: 4,
                    mt: 2,
                    backgroundColor: "#EAF4FF",
                    width: "100%",
                    height: "119px",
                    position: "relative",
                    gap: 2,
                  }}
                  onClick={handleRun2Click}
                >
                  <SecurityUploadIcon width={24} height={24} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        color: "black",
                        ":hover": { textDecoration: "underline" },
                      }}
                    >
                      Drop your Run-2 SAST report here or click to browse
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Supports XLSX format. Upload a file
                    </Typography>
                  </Box>
                  <input
                    ref={fileInputRef2}
                    type="file"
                    accept=".xlsx,.xls,"
                    hidden
                    onChange={handleRun2FileChange}
                  />
                </Paper>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "2px solid #90caf9",
                      mb: 2,
                      backgroundColor: "#EAF4FF",
                      width: "100%",
                      position: "relative",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <BusinessDocIcon width={24} height={24} />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "black",
                          }}
                        >
                          {run2File.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", color: "#00000080" }}
                        >
                          {(run2File.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="delete"
                      onClick={handleRemoveRun2File}
                    >
                      <DeleteIcon sx={{ color: "#f44336" }} />
                    </IconButton>
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              disabled={
                (scanner === "Single Run Analysis" && !run1File) ||
                (scanner === "Comparison Analysis" && (!run1File || !run2File))
              }
              sx={{
                color: "#FFFFFF",
                backgroundColor: "#4DBD74",
                textTransform: "none",
                width: "256px",
                height: "50px",
                borderRadius: "8px",
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
              onClick={handleInitiateScan}
            >
              {scanner === "Single Run Analysis"
                ? "Start Single Run Analysis"
                : "Start Comparison Analysis"}
            </Button>
          </Box>
        </Box>
      </Box>

      {openErrorPopup && (
        <MessagePopup
          modalOpen={openErrorPopup}
          okayHandler={() => setOpenErrorPopup(false)}
          message={errorMessage}
        />
      )}
      {sessionExpired && <SessionExpired />}
    </Box>
  );
};

export default SASTInsightsInputComponent;
